import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSidebarStore, useAuthStore } from '@hooks/useStores';
import { useToastContext } from '@contexts/ToastContext';
import { useModal } from '@hooks/useModal';
import $api from '@http';

interface UseNoteCardActionsProps {
  noteId: string;
  noteTitle: string;
  isPublic: boolean;
  onDelete?: () => void;
}

export const useNoteCardActions = ({
  noteId,
  noteTitle,
  isPublic: initialIsPublic,
  onDelete,
}: UseNoteCardActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sidebarStore = useSidebarStore();
  const authStore = useAuthStore();
  const toast = useToastContext();
  const { modalState, showModal, closeModal } = useModal();
  const [isPublic, setIsPublic] = useState<boolean>(initialIsPublic);
  const isActivated = authStore.user?.isActivated ?? false;

  const handleRename = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isActivated) {
      toast.warning(t('noteMenu.activateEdit'));
      return;
    }
    navigate(`/note/${noteId}`);
    setTimeout(() => {
      sidebarStore.startEditing(noteId, 'rename');
    }, 100);
  }, [isActivated, navigate, noteId, sidebarStore, t, toast]);

  const handleCreateSubnote = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isActivated) {
      toast.warning(t('noteMenu.activateCreateNotes'));
      return;
    }
    navigate(`/note/${noteId}`);
    setTimeout(() => {
      sidebarStore.startEditing(`temp-subnote-${Date.now()}`, 'create-subnote', noteId);
    }, 100);
  }, [isActivated, navigate, noteId, sidebarStore, t, toast]);

  const handleTogglePublic = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isActivated) {
      toast.warning(t('noteMenu.activateMakePublic'));
      return;
    }

    const makePublic = !isPublic;

    try {
      await $api.put(`/notes/${noteId}`, { isPublic: makePublic });
      setIsPublic(makePublic);
      toast.success(makePublic ? t('noteMenu.madePublic') : t('noteMenu.madePrivate'));
    } catch (err: any) {
      console.error('Failed to toggle public state:', err);
    }
  }, [isActivated, isPublic, noteId, t, toast]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isActivated) {
      toast.warning(t('noteMenu.activateDelete'));
      return;
    }

    showModal(
      t('noteMenu.deleteConfirmTitle'),
      t('noteMenu.deleteConfirmMessage', { title: noteTitle }),
      async () => {
        try {
          await $api.delete(`/notes/${noteId}`);
          toast.success(t('noteMenu.noteDeleted'));
          if (onDelete) {
            onDelete();
          }
        } catch (err: any) {
          console.error('Failed to delete note:', err);
        }
      },
      {
        confirmText: t('noteMenu.delete'),
        cancelText: t('common.cancel'),
        variant: 'danger',
      }
    );
  }, [isActivated, noteId, noteTitle, onDelete, showModal, t, toast]);

  return {
    isPublic,
    modalState,
    closeModal,
    handleRename,
    handleCreateSubnote,
    handleTogglePublic,
    handleDelete,
  };
};
